import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { BannerService } from '../services/APIService';

// Async thunks for API calls
export const fetchBanners = createAsyncThunk(
  'banners/fetchBanners',
  async (_, { rejectWithValue }) => {
    try {
      const response = await BannerService.getAllBanners();
      return response;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const fetchBannerById = createAsyncThunk(
  'banners/fetchBannerById',
  async (bannerId, { rejectWithValue }) => {
    try {
      const response = await BannerService.getBanner(bannerId);
      return response;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const createBanner = createAsyncThunk(
  'banners/createBanner',
  async (bannerData, { rejectWithValue }) => {
    try {
      const response = await BannerService.createBanner(bannerData);
      return response;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const updateBanner = createAsyncThunk(
  'banners/updateBanner',
  async ({ bannerId, bannerData }, { rejectWithValue }) => {
    try {
      const response = await BannerService.updateBanner(bannerId, bannerData);
      return response;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const deleteBanner = createAsyncThunk(
  'banners/deleteBanner',
  async (bannerId, { rejectWithValue }) => {
    try {
      await BannerService.deleteBanner(bannerId);
      return bannerId;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

const bannerSlice = createSlice({
  name: 'banners',
  initialState: {
    banners: [],
    currentBanner: null,
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
  },
  reducers: {
    clearCurrentBanner: (state) => {
      state.currentBanner = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchBanners
      .addCase(fetchBanners.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchBanners.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Transform the API response to match the format expected by the HeroBanner component
        if (action.payload && action.payload.data) {
          state.banners = action.payload.data.map(banner => ({
            id: banner.id,
            title: banner.title,
            description: banner.description,
            image: banner.hasImage ? banner.imageData : null,
            createdAt: banner.createdAt,
            updatedAt: banner.updatedAt
          }));
        } else {
          state.banners = [];
        }
      })
      .addCase(fetchBanners.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Handle fetchBannerById
      .addCase(fetchBannerById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchBannerById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Transform the API response for a single banner
        if (action.payload && action.payload.data) {
          const banner = action.payload.data;
          state.currentBanner = {
            id: banner.id,
            title: banner.title,
            description: banner.description,
            image: banner.hasImage ? banner.imageData : null,
            createdAt: banner.createdAt,
            updatedAt: banner.updatedAt
          };
        } else {
          state.currentBanner = null;
        }
      })
      .addCase(fetchBannerById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Handle createBanner
      .addCase(createBanner.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createBanner.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Transform the banner data from the response and add to the state
        if (action.payload && action.payload.data) {
          const banner = action.payload.data;
          state.banners.push({
            id: banner.id,
            title: banner.title,
            description: banner.description,
            image: banner.hasImage ? banner.imageData : null,
            createdAt: banner.createdAt,
            updatedAt: banner.updatedAt
          });
        }
      })
      .addCase(createBanner.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Handle updateBanner
      .addCase(updateBanner.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateBanner.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Transform the banner data from the response and update the state
        if (action.payload && action.payload.data) {
          const banner = action.payload.data;
          const transformedBanner = {
            id: banner.id,
            title: banner.title,
            description: banner.description,
            image: banner.hasImage ? banner.imageData : null,
            createdAt: banner.createdAt,
            updatedAt: banner.updatedAt
          };
          
          const index = state.banners.findIndex((b) => b.id === transformedBanner.id);
          if (index !== -1) {
            state.banners[index] = transformedBanner;
          }
          state.currentBanner = transformedBanner;
        }
      })
      .addCase(updateBanner.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Handle deleteBanner
      .addCase(deleteBanner.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteBanner.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.banners = state.banners.filter((banner) => banner.id !== action.payload);
        if (state.currentBanner && state.currentBanner.id === action.payload) {
          state.currentBanner = null;
        }
      })
      .addCase(deleteBanner.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { clearCurrentBanner } = bannerSlice.actions;

// Selectors
export const selectAllBanners = (state) => state.banners.banners;
export const selectBannerById = (state, bannerId) =>
  state.banners.banners.find((banner) => banner.id === bannerId);
export const selectCurrentBanner = (state) => state.banners.currentBanner;
export const selectBannersStatus = (state) => state.banners.status;
export const selectBannersError = (state) => state.banners.error;

export default bannerSlice.reducer;
